import { supabase } from './api'

/**
 * Links attached to a task or an issue.
 *
 * URLs only — no file upload. See 0013's header for why (the 500 MB database
 * ceiling, the uploaded-file XSS class, and not wanting a second copy of the
 * auth rules inside an Edge Function).
 *
 * Who may add or remove one is decided in the database by `can_link`: on a
 * task, anyone it is assigned to plus admins; on an issue, whoever reported it
 * plus admins.
 */

export interface LinkRow {
  id: string
  url: string
  label: string | null
  added_by_id: string | null
  added_by_name: string
}

function fail(error: { code?: string; message?: string }): Error {
  if (error.code === '28000') {
    return new Error('This link is not recognised. Ask Scott for a new one.')
  }
  if (error.code === '42501') {
    return new Error(error.message || 'You do not have permission to do that.')
  }
  if (error.code === '22023') {
    return new Error(error.message || 'That link was not accepted.')
  }
  return new Error(error.message || 'Something went wrong. Try again.')
}

export async function addLink(
  token: string,
  owner: { taskId?: string | undefined; issueId?: string | undefined },
  url: string,
  label: string | null,
): Promise<string> {
  const { data, error } = await supabase.rpc('add_link', {
    p_token: token,
    p_task_id: owner.taskId ?? null,
    p_issue_id: owner.issueId ?? null,
    p_url: url,
    p_label: label,
  })
  if (error) throw fail(error)
  return data as string
}

export async function removeLink(token: string, linkId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_link', {
    p_token: token,
    p_link_id: linkId,
  })
  if (error) throw fail(error)
}

/**
 * Defence in depth on rendering.
 *
 * The database already refuses anything that is not http/https — see the
 * `links_url_scheme` constraint, which is the actual guarantee. This exists so
 * that a row which somehow got in another way still cannot become a
 * `javascript:` href on an origin whose localStorage holds the sign-in token.
 */
export function safeHref(url: string): string {
  try {
    const u = new URL(url)
    return u.protocol === 'http:' || u.protocol === 'https:' ? url : '#'
  } catch {
    return '#'
  }
}

/**
 * Where a link actually goes, shown separately from its label.
 *
 * Worth the space: this list is visible to the whole shop and anyone can add to
 * their own work, so "Wiring diagram" pointing at somewhere unexpected should
 * be visible before somebody taps it, not after.
 */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/** The label if there is one, otherwise something readable from the URL itself. */
export function linkTitle(link: LinkRow): string {
  if (link.label) return link.label
  try {
    const u = new URL(link.url)
    const tail = u.pathname.split('/').filter(Boolean).pop()
    return tail ? decodeURIComponent(tail) : hostOf(link.url)
  } catch {
    return link.url
  }
}
